import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { createRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-use';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import ReCAPTCHA from 'react-google-recaptcha';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useHistory } from 'react-router';
import isEmpty from 'lodash.isempty';
import { IconList } from '../components/IconList/IconList';
import IconListItem from '../components/IconList/IconListItem';
import BaseLayoutCta from '../components/BaseLayoutCta/BaseLayoutCta';
import Paragraph from '../components/Paragraph/Paragraph';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../util/helpers/sessionStorageHelper';
import { JWT_TOKEN } from '../constants/localStorage';
import {
  fetchDecryptedEmail,
  fetchEncryptedEmail,
  fetchUsernames,
  logoutUser,
} from '../store/actions/user/userActions';
import {
  APPLICATION_TYPE,
  ACCOUNT_TYPE,
  LANDING_PAGE_URL,
  OFFER_CODE,
  REGISTRATION_FLOW_TYPE,
  IS_SALES_ASSESSTED_REGISTRATION_FLOW,
  SALES_ASSISTED_QUERY_DATA,
} from '../constants/sessionStorage';
import { REGISTRATION_FLOW_TYPES } from '../util/constants';
import { isAipRegistrationFlow } from '../util/helpers/registrationFlowTypeHelper';
import { postTrackingDetails } from '../store/actions/affiliate/afilliateActions';
import {
  getAffParam,
  getCJeventParam,
  getOfferCodeParam,
  getPromotionParam,
} from '../util/helpers/referrerHelpers';
import useQuery from '../util/hooks/useQuery';
import CashPaymentBag from '../components/Icons/CashPaymentBag';
import BaseLayout from '../components/BaseLayout';
import { Heading } from '../components/Heading';
import {
  selectIsReCaptchaEnabled,
  selectRegistrationUrl,
  selectSettings,
} from '../store/selectors/settingsSelectors';
import themeColors from '../assets/styles/themeColors';
import VaultLock from '../components/Icons/VaultLock';
import Clock from '../components/Icons/ClockIcon';
import {
  FormContainer,
  FormWrap,
} from '../components/FormContainer/FormContainer';
import { getValidationSchema } from '../util/helpers/getValidationSchema';
import InputText from '../components/Fields/InputText';
import config from '../config';
import ChevronRight from '../components/Icons/ChevronRight';
import ChevronDown from '../components/Icons/ChevronDown';
import { PAGES } from '../constants/pages';
import SectionLoader from '../components/SectionLoader/SectionLoader';

const DescriptionContainer = styled.a`
  display: grid;
  grid-template-columns: 0.7fr auto;
  grid-gap: 0.5rem;
  align-items: center;
  margin: 2.5rem 0 2rem;
`;

const DescriptionLink = styled.a`
  color: ${themeColors.colorTextSecondary};
  text-decoration: none;
  font-weight: 400;
  bottom: 60px;
  text-decoration: underline;
  align-items: center;
  justify-content: center;
  text-align: start;
  @media only screen and (max-width: 900px) {
    margin: 0;
  }
`;

const WelcomeFluentEmail = ({ registrationFlowType }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const query = useQuery();
  const reCaptchaRef = createRef();
  const { logonUid, username, brokerCode, user } = query;
  const referrerUrl = document.referrer;
  const landingPageUrl = window.location.href;
  const referrerAff = getAffParam();
  const referrerCJEvent = getCJeventParam();
  const referrerPromotionCode = getPromotionParam();
  const reffererOfferCode = getOfferCodeParam();
  const settings = useSelector(selectSettings);
  const isReCaptchaEnabled = useSelector(selectIsReCaptchaEnabled);
  const [showInformationList, setShowInformationList] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const RegistrationUrl = useSelector(selectRegistrationUrl);
  const formValidationSchema = yup.object({
    ...getValidationSchema({
      fields: ['Email'],
    }),
  });

  const { handleSubmit, control, setValue } = useForm({
    resolver: yupResolver(formValidationSchema),
  });

  const { BrokerName, BrokerCode } = settings;

  const { search } = window?.location;
  const params = new URLSearchParams(search);
  const emailFromParams = params.get('email');
  if (emailFromParams && emailFromParams.length > 0) {
    setValue('Email', emailFromParams);
  }
  const [emailInvalidMessage, setEmailInvalidMessage] = useState('');

  const promoCodeFromParams = params.get('PromoCode');
  if (promoCodeFromParams && promoCodeFromParams.length > 0) {
    setValue('referralCode', promoCodeFromParams);
  }

  const referrerEmail = params.get('ReferrerEmail');
  const refereeEmail = params.get('RefereeEmail');

  if (refereeEmail && refereeEmail.length > 0) {
    setValue('Email', refereeEmail);
  }

  const welcomeDescriptions = [
    {
      title: t('welcomeFluent.applyInMinutes.title'),
      description: t('welcomeFluent.applyInMinutes.description', {
        brokerCode: BrokerCode,
      }),
      Icon: Clock,
    },
    {
      title: isAipRegistrationFlow(registrationFlowType)
        ? t('welcomeFluent.setIt.title')
        : t('welcomeFluent.brokerage.title'),
      description: isAipRegistrationFlow(registrationFlowType)
        ? t('welcomeFluent.setIt.description', {
            brokerCode: BrokerCode,
          })
        : t('welcomeFluent.brokerage.description', {
            brokerCode: BrokerCode,
          }),
      Icon: CashPaymentBag,
    },
    {
      title: t('welcomeFluent.international.title'),
      description: t('welcomeFluent.international.description'),
      Icon: VaultLock,
    },
  ];

  useEffect(() => {
    const JwtToken = retrieveFromSessionStorage(JWT_TOKEN);

    if (JwtToken) {
      dispatch(logoutUser());
    }
  }, [dispatch]);

  useEffect(() => {
    if (
      !location.pathname.includes('/createAccount') &&
      !location.pathname.includes('/login')
    ) {
      if (location.pathname.includes('/aip')) {
        storeInSessionStorage(
          REGISTRATION_FLOW_TYPE,
          REGISTRATION_FLOW_TYPES.AIP,
        );
        storeInSessionStorage(APPLICATION_TYPE, 'Individual');
        removeFromSessionStorage(ACCOUNT_TYPE);
        return;
      }
      storeInSessionStorage(
        REGISTRATION_FLOW_TYPE,
        REGISTRATION_FLOW_TYPES.REGULAR,
      );
    }
  }, [location.pathname]);

  useEffect(() => {
    if (brokerCode && logonUid && username) {
      storeInSessionStorage(IS_SALES_ASSESSTED_REGISTRATION_FLOW, true);
      storeInSessionStorage(SALES_ASSISTED_QUERY_DATA, {
        brokerCode,
        logonUid,
        username,
      });
    }
  }, [brokerCode, logonUid, username]);

  useEffect(() => {
    const { aff, p, cjevent, offer, a } = query;
    if (offer) {
      storeInSessionStorage(OFFER_CODE, offer);
    }
    if (reffererOfferCode) {
      storeInSessionStorage(OFFER_CODE, reffererOfferCode);
    }
    storeInSessionStorage(LANDING_PAGE_URL, landingPageUrl);
    setTimeout(() => {
      dispatch(
        postTrackingDetails({
          data: {
            AffiliateCode: aff || referrerAff || a,
            CJEvent: cjevent || referrerCJEvent,
            PromotionCode: p || referrerPromotionCode,
            ReferrerUrl: referrerUrl,
          },
        }),
      );
    }, 1000);
  }, []); //eslint-disable-line

  useEffect(() => {
    if (user && !isEmpty(settings)) {
      dispatch(
        fetchDecryptedEmail({
          data: {
            encryptedUserUid: decodeURIComponent(user),
          },
          fetchUsernamesCall,
        }),
      );
    }
  }, [settings]); //eslint-disable-line

  const redirectToV1 = (email) => {
    window.location.href = `${RegistrationUrl}/r/start?user=${encodeURIComponent(
      email,
    )}`;
  };

  const encryptV1Email = (email) => {
    dispatch(
      fetchEncryptedEmail({
        data: {
          email,
        },
        redirectToV1,
      }),
    );
  };

  const onSubmit = async (data) => {
    const onSuccess = () => {
      history.push(PAGES.LOGIN_PATH);
    };
    const onError = (error) => {
      if (error?.type === 'EmailNotValid') {
        setEmailInvalidMessage(error.message);
      } else {
        history.push(PAGES.CREATE_ACCOUNT);
      }
    };
    if (isReCaptchaEnabled) {
      const token = await reCaptchaRef.current.executeAsync();
      reCaptchaRef.current.reset();
      setIsLoading(true);
      dispatch(
        fetchUsernames({
          email: data?.Email.trim(),
          token,
          setIsLoading,
          onSuccess,
          onError,
          encryptV1Email,
          referralCode: promoCodeFromParams,
          referrerEmail,
        }),
      );
      return;
    }
    setIsLoading(true);
    dispatch(
      fetchUsernames({
        email: data?.Email.trim(),
        setIsLoading,
        onSuccess,
        onError,
        encryptV1Email,
        referralCode: promoCodeFromParams,
        referrerEmail,
      }),
    );
  };

  const fetchUsernamesCall = async ({ email }) => {
    const onSuccess = () => {
      history.push(PAGES.LOGIN_PATH);
    };
    const onError = () => {
      history.push(PAGES.CREATE_ACCOUNT);
    };
    if (isReCaptchaEnabled) {
      const token = await reCaptchaRef.current.executeAsync();
      reCaptchaRef.current.reset();
      setIsLoading(true);
      dispatch(
        fetchUsernames({
          email,
          token,
          setIsLoading,
          onSuccess,
          onError,
          encryptV1Email,
          referralCode: promoCodeFromParams,
          referrerEmail,
        }),
      );
      return;
    }
    dispatch(
      fetchUsernames({
        email,
        setIsLoading,
        onSuccess,
        onError,
        encryptV1Email,
        referralCode: promoCodeFromParams,
        referrerEmail,
      }),
    );
  };

  const onShowList = () => {
    setShowInformationList(!showInformationList);
  };

  return (
    <BaseLayout noLogoBox dataCy="container-new-account">
      <Helmet>
        <title>
          {t('welcomeFluent.titleTag', {
            brokerName: BrokerName,
          })}
        </title>
      </Helmet>
      <Heading fontWeight={700} mb={8}>
        {t('welcomeFluent.title')}
      </Heading>
      <Paragraph mb={8} color={themeColors.colorTextXLLight}>
        {t('welcomeFluent.subtitleEmail', { brokerName: BrokerName })}
      </Paragraph>
      {isAipRegistrationFlow(registrationFlowType) && (
        <Paragraph mb={8} color={themeColors.colorTextXLLight}>
          {t('welcomeFluent.metalstreamSubtitle', { brokerCode: BrokerCode })}
        </Paragraph>
      )}
      <SectionLoader isLoading={isLoading}>
        <FormWrap mt={20}>
          <FormContainer maxWidth={344}>
            <Controller
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState }) => {
                const validationError = fieldState?.error?.message;
                const combinedError = validationError || emailInvalidMessage;
                return (
                  <InputText
                    label={t('welcomeFluent.emailLabel')}
                    type="email"
                    {...field}
                    error={combinedError && { message: combinedError }}
                    autoFocus
                    autoComplete="username"
                    data-cy="input-registration-email"
                  />
                );
              }}
              name="Email"
              defaultValue=""
            />
            {promoCodeFromParams && (
              <Controller
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field, fieldState }) => (
                  <InputText
                    label={t('welcomeFluent.promoCode')}
                    type="text"
                    {...field}
                    {...fieldState}
                    autoFocus
                    data-cy="input-registration-promocode"
                    disabled
                  />
                )}
                name="referralCode"
                defaultValue=""
              />
            )}
            {isReCaptchaEnabled && (
              <ReCAPTCHA
                ref={reCaptchaRef}
                sitekey={config.recaptcha.siteKey}
                size="invisible"
              />
            )}
          </FormContainer>
          <BaseLayoutCta
            buttonProps={{
              type: 'submit',
              title: t('welcomeFluent.conitnue'),
              onClick: handleSubmit(onSubmit),
            }}
          />
        </FormWrap>
      </SectionLoader>

      <DescriptionContainer onClick={() => onShowList()}>
        <DescriptionLink>
          {t('welcomeFluent.emailCta', { brokerName: BrokerName })}
        </DescriptionLink>
        {showInformationList ? <ChevronDown /> : <ChevronRight />}
      </DescriptionContainer>
      {showInformationList && (
        <IconList mt={20}>
          {welcomeDescriptions.map(({ title, description, Icon }) => (
            <IconListItem
              key={title}
              title={title}
              description={description}
              Icon={Icon}
            />
          ))}
        </IconList>
      )}
    </BaseLayout>
  );
};

WelcomeFluentEmail.propTypes = {
  registrationFlowType: PropTypes.string,
};

export default WelcomeFluentEmail;
